import { inject, Injectable } from '@angular/core';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { ConfirmActionModalComponent } from '@app/shared/components/confirm-action-modal/confirm-action-modal.component';
import { translate } from '@ngneat/transloco';
import { PlcardCreateModalComponent } from '@app/shared/components/plcard-create-modal/plcard-create-modal.component';
import { PlcardModalService } from '@app/pages/pl-cards/plcard-modal.service';
import { CarouselModalComponent } from '@app/shared/components/gallery/components/carousel-modal/carousel-modal.component';
import { GalleryMedia } from '@app/shared/components/gallery/components/gallery';
import { round } from 'lodash-es';
import { downloadBlob } from '@app/shared/helpers/download.helper';
import { DownloadModalComponent } from '@app/shared/components/modals/download-modal.component';
import { ProcessErrorModalComponent } from '@app/shared/components/modals/process-error-modal.component';
import { ProcessingModalComponent } from '@app/shared/components/modals/processing-modal.component';
import { SupportDetailComponent } from '@app/shared/components/modals/support-detail/support-detail.component';
import { ExportProductsComponent } from '@app/shared/components/modals/export-products/export-products.component';
import { CrmTableLoadFilters } from 'common-module/table/model/table-type-data';

export interface PlCardModalParams {
  data?: any;
  multiProduct?: boolean;
  onSubmit: (data?: OnSubmitParams) => void;
  onCancel?: () => void;
}

export interface OnSubmitParams {
  file: any;
  existingPlCard: string;
  productIds: string[];
}

@Injectable({ providedIn: 'root' })
export class CommonModalsService {
  private modalService = inject(NzModalService);
  private plcardModalService = inject(PlcardModalService);

  closeAll() {
    this.modalService.closeAll();
  }

  createCarouselModal(images: GalleryMedia[], selectedImage: string) {
    this.modalService.create({
      nzTitle: undefined,
      nzFooter: undefined,
      nzWidth: 800,
      nzNoAnimation: true,
      nzClassName: 'carousel-modal',
      nzContent: CarouselModalComponent,
      nzData: {
        images,
        selectedImage,
      },
    });
  }

  createRemoveCardModal(onOk: () => void, onCancel?: () => void): NzModalRef {
    const modal = this.modalService.create({
      nzTitle: undefined,
      nzFooter: null,
      nzWidth: 620,
      nzNoAnimation: true,
      nzContent: ConfirmActionModalComponent,
      nzData: {
        title: 'modal.removeItem.title',
        body: 'modal.removeItem.content',
        confirmText: 'modal.removeItem.actions.confirm',
        confirmBtnType: 'warning',
        cancelText: 'modal.removeItem.actions.cancel',
        handleConfirmClick: () => {
          onOk();
          modal.close();
        },
        handleCancelClick: () => {
          onCancel?.();
          modal.close();
        },
      },
    });
    return modal;
  }

  createConfirmUploadModal(onOk: () => void, onCancel?: () => void): void {
    const modal = this.modalService.create({
      nzTitle: undefined,
      nzFooter: undefined,
      nzWidth: 620,
      nzNoAnimation: true,
      nzContent: ConfirmActionModalComponent,
      nzData: {
        title: 'modal.confirmUpload.title',
        body: 'modal.confirmUpload.content',
        confirmText: 'modal.confirmUpload.actions.confirm',
        cancelText: 'modal.confirmUpload.actions.cancel',
        confirmButtonStyle: {
          'background-color': '#ef9c53',
          border: '1px solid #ef9c53',
        },
        handleConfirmClick: () => {
          onOk();
          modal.close();
        },
        handleCancelClick: () => {
          if (onCancel) {
            onCancel();
          }
          modal.close();
        },
      },
    });
  }

  editPlCardModal(conf: PlCardModalParams) {
    const { onCancel, onSubmit, multiProduct } = conf;
    const modalRef = this.modalService.create({
      nzTitle: translate('modal.plCard.new.title'),
      nzContent: PlcardCreateModalComponent,
      nzFooter: null,
      nzData: {
        multiProduct,
        onCancel: () => {
          if (onCancel) {
            return onCancel();
          }
          modalRef.close();
        },
        onSubmit: (inst: PlcardCreateModalComponent) => {
          const submitData: any = {
            productIds: inst.productCodes,
            file: inst.file,
            existingPlCard: inst.existingCard,
          };

          this.plcardModalService.createConfirmUploadModal(() => {
            onSubmit?.(submitData);
          });
        },
      },
      nzWidth: 730,
      nzClosable: true,
      nzMaskClosable: true,
    });
    return modalRef;
  }

  downloadModal(
    res: Blob,
    filename: string,
    fileFormat: string,
    onClose?: () => void,
  ) {
    const modal = this.modalService.create({
      nzTitle: 'Your file is ready to be downloaded',
      nzFooter: null,
      nzContent: DownloadModalComponent,
      nzData: {
        fileSize: round(res.size / 1000),
        format: fileFormat,
        onDownload: () => {
          downloadBlob(res, filename, fileFormat);
        },
      },
      nzOnCancel: () => {
        modal.close();
        onClose?.();
      },
    });
  }

  public errorModal(onRetry: () => void) {
    return this.modalService.create({
      nzTitle: 'Something went wrong',
      nzFooter: null,
      nzContent: ProcessErrorModalComponent,
      nzData: {
        onRetry: () => {
          this.modalService.closeAll();
          onRetry();
        },
      },
      nzOnCancel: () => this.modalService.closeAll(),
    });
  }

  public processingModal(onTerminate?: () => void) {
    return this.modalService.create({
      nzTitle: 'Please wait...',
      nzContent: ProcessingModalComponent,
      nzFooter: null,
      // onCancelAction: () => this.modalService.closeAll(),
      nzData: {
        onTerminate: () => {
          onTerminate?.();
        },
      },
      nzOnCancel: () => this.modalService.closeAll(),
    });
  }

  public createRemoveItemModal(onOk: () => void, onCancel?: () => void): void {
    const modal = this.modalService.create({
      nzTitle: null,
      nzFooter: null,
      nzWidth: 620,
      nzNoAnimation: true,
      nzContent: ConfirmActionModalComponent,
      nzData: {
        title: 'modal.removeItem.title',
        body: 'modal.removeItem.content',
        confirmText: 'modal.removeItem.actions.confirm',
        cancelText: 'modal.removeItem.actions.cancel',
        handleConfirmClick: () => {
          onOk();
          modal.close();
        },
        handleCancelClick: () => {
          if (onCancel) {
            onCancel();
          }
          modal.close();
        },
      } as Partial<ConfirmActionModalComponent>,
    });
  }

  public supportModal() {
    const modal = this.modalService.create({
      nzTitle: null,
      nzFooter: null,
      nzContent: SupportDetailComponent,
      nzData: {
        onSubmit: () => {
          modal.close();
        },
      },
    });
    return modal;
  }

  public exportProducts(listFilters: CrmTableLoadFilters) {
    this.modalService.create({
      nzContent: ExportProductsComponent,
      nzTitle: translate('modal.export.title'),
      nzFooter: null,
      nzWidth: 730,
      nzNoAnimation: true,
      nzData: {
        listFilters,
      } as Partial<ExportProductsComponent>,
    });
  }
}
